// CLAIM CALCULATOR RATES
// =============================================================
// All dollar values are Florida market rates as of 2026, calibrated
// toward the higher end of defensible ranges (replacement cost,
// proper code upgrades, market labor). These produce the LOW number;
// the HIGH range multiplier below pushes the upper bound.
//
// EDIT THESE NUMBERS ANYTIME — the calculator reads from this file.
// =============================================================

// How much to multiply the "low" estimate by to produce the "high" estimate
// 1.45 = high estimate is 45% above the low estimate
export const RANGE_MULTIPLIER = 1.45;

// Where to put the "Likely recovery" highlight inside the range
// 0.55 = 55% of the way between low and high
export const LIKELY_POSITION = 0.55;

// -------------------------------------------------------------
// ROOF DAMAGE
// -------------------------------------------------------------
// Per "square" (100 sqft of roof). FULL tear-off + replacement assumed.
// These rates bake in: tear-off, decking touch-up, underlayment, code
// upgrades (drip edge, secondary water barrier), permits, dump fees.
export const ROOF_PER_SQUARE = {
  asphalt: 1100, // Architectural shingle — full replacement
  tile: 2500, // Concrete/clay tile — full replacement
  metal: 1900, // Standing-seam metal — full replacement
  flat: 1500, // TPO / Mod Bit — full replacement
};

// Roof-area conversion: roof footprint ≈ living-area sqft × this ratio.
// 1.15 covers overhangs and typical FL gable pitch for single-story.
export const ROOF_HOUSE_TO_ROOF_RATIO = 1.15;

// Roof add-ons that still require homeowner input
export const ROOF_ADDONS = {
  gutterReplace: 22, // per linear foot of gutter
};

// -------------------------------------------------------------
// INTERIOR DAMAGE (drywall, paint, flooring, baseboards, mold)
// -------------------------------------------------------------
export const INTERIOR_PER_ROOM = {
  // Average room ~ 12x12. Includes drywall replace, paint, baseboards,
  // and flooring replacement when applicable.
  minor: 2800, // surface paint + minor drywall
  moderate: 6500, // partial drywall replace, full repaint, baseboards
  major: 13500, // full gut, drywall, insulation, paint, flooring
};

export const INTERIOR_FLOORING_PER_SQFT = {
  carpet: 6,
  laminate: 9,
  vinyl: 11,
  tile: 16,
  hardwood: 22,
};

export const INTERIOR_MOLD_REMEDIATION = {
  small: 3500, // contained area, single room
  medium: 9500, // multiple rooms, HVAC affected
  large: 22000, // whole-home or hidden cavity remediation
};

// -------------------------------------------------------------
// WINDOWS
// -------------------------------------------------------------
export const WINDOW_PER_UNIT = {
  standard: 950, // single-hung vinyl, R&R
  impact: 1700, // impact-rated, code-compliant (PGT, CWS, Eastern)
  largePicture: 2400, // sliders, picture windows, oversized
};

// -------------------------------------------------------------
// SIDING
// -------------------------------------------------------------
// Per square foot of siding INSTALLED — includes tear-off, house wrap,
// trim, fasteners, paint where applicable.
export const SIDING_PER_SQFT = {
  vinyl: 11,
  hardie: 17, // James Hardie fiber cement
  stucco: 14, // patch + paint
  wood: 13,
  brick: 25, // repair / replace
};

// -------------------------------------------------------------
// CABINETS & COUNTERTOPS
// -------------------------------------------------------------
export const CABINET_PER_LINEAR_FOOT = {
  builderGrade: 350,
  midGrade: 600,
  custom: 1100,
};

export const COUNTERTOP_PER_SQFT = {
  laminate: 55,
  granite: 110,
  quartz: 135,
  marble: 170,
};

// -------------------------------------------------------------
// SCREEN ENCLOSURE (pool cage / lanai)
// -------------------------------------------------------------
// Per square foot of cage footprint (covered + screen)
export const SCREEN_ENCLOSURE_PER_SQFT = {
  rescreen: 5, // mesh only
  partialRebuild: 22, // some structural
  fullRebuild: 38, // full new cage to current FL code
};

// Door / kickplate add-ons
export const SCREEN_ENCLOSURE_DOOR = 650;

// -------------------------------------------------------------
// A/C (HVAC) DAMAGE
// -------------------------------------------------------------
export const AC_SYSTEM = {
  airHandlerOnly: 4500,
  condenserOnly: 5500,
  fullSystemSmall: 9500, // up to 2.5 ton
  fullSystemMedium: 13000, // 3 - 4 ton
  fullSystemLarge: 17500, // 5+ ton or dual-system
};

export const AC_DUCTWORK = {
  partial: 3800,
  whole: 9000,
};

// -------------------------------------------------------------
// GARAGE DOOR
// -------------------------------------------------------------
export const GARAGE_DOOR = {
  singleStandard: 1800,
  singleImpact: 3500,
  doubleStandard: 3200,
  doubleImpact: 6200,
  openerAdd: 750, // add for opener
};

// -------------------------------------------------------------
// FENCE
// -------------------------------------------------------------
// Per linear foot installed (Florida market)
export const FENCE_PER_LINEAR_FOOT = {
  chainLink: 22,
  wood: 42,
  vinyl: 62,
  aluminum: 55,
};

export const FENCE_GATE = 450; // per gate

// -------------------------------------------------------------
// CONTENTS (personal property)
// -------------------------------------------------------------
// Multiplier applied to the homeowner's estimate of damaged contents.
// PA's typically recover above what the homeowner first estimates
// because they include depreciation buyback, tax, and proper RCV.
export const CONTENTS_RECOVERY_MULTIPLIER = 1.35;

// Minimum to show for any contents claim
export const CONTENTS_MINIMUM = 1500;

// -------------------------------------------------------------
// "I DON'T KNOW" — Florida-average fallback values
// Used when a homeowner checks the "I don't know" box on any
// numeric input. These are honest mid-range Florida defaults.
// -------------------------------------------------------------
export const UNKNOWN_DEFAULTS = {
  houseSqft: 2000, // average single-family FL home living area
  gutterFeet: 140, // typical perimeter gutter run
  roomsAffected: 3, // typical multi-room water/storm event
  floorSqft: 400, // ~2 average rooms or 1 large area
  windowCount: 6, // typical multi-window damage event
  sidingSqft: 350, // ~1 wall affected
  cabinetLinearFeet: 18, // typical kitchen run
  counterSqft: 35, // typical kitchen counter
  screenEnclosureSqft: 1200, // typical FL pool cage
  doorsNeeded: 1,
  fenceLinearFeet: 100, // typical residential run
  gates: 1,
  contentsValue: 8000, // mid-range contents loss
};

// -------------------------------------------------------------
// DEDUCTIBLE / PA FEE NOTE
// -------------------------------------------------------------
// We DO NOT subtract the deductible from the estimate — we show the
// gross claim value because that's how PAs sell. The fee disclosure
// is shown on the results page in fine print.
